/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { Button } from "@mui/material";
import React from "react";
import { AdvocardFlexAiResponse } from "../types/AdvocardFlexAiResponse";
import { getProductData } from "./productService";
import ButtonLoading from "../components/Button/ButtonLoading";
import { AbstractCase } from "../types/Entities/AbstractCase";
import { Customer } from "../types/Entities/Customer";
import { Link } from "react-router";

const contractConfig = {
  createCase: true,
  lpLink: null,
  buttonLabel: "Erstberatung im Vertragsrecht",
};

export const productConfig = {
  death: {
    createCase: true,
    lpLink: null,
    buttonLabel: "Erstberatung im Familienrecht",
  },
  familyLawCase: {
    createCase: true,
    lpLink: null,
    buttonLabel: "Erstberatung im Familienrecht",
  },
  divorce: {
    createCase: false,
    lpLink: "/lp/advocard/scheidung",
    buttonLabel: "Erstberatung zur Scheidung",
  },
  alimony: {
    createCase: true,
    lpLink: null,
    buttonLabel: "Erstberatung zum Unterhalt",
  },
  settlement: {
    createCase: false,
    lpLink: "/lp/advocard/arbeitsrecht",
    buttonLabel: "Professionelle Hilfe bei Kündigung",
  },
  eventContract: contractConfig,
  membershipContract: contractConfig,
  otherContract: contractConfig,
  providerContract: contractConfig,
  purchaseContract: contractConfig,
  serviceContract: contractConfig,
  subscriptionContract: contractConfig,
  travelContract: contractConfig,
  vehicleContract: contractConfig,
  traffic: {
    createCase: false,
    lpLink: "/lp/advocard/bussgeld-fahrverbot",
    buttonLabel: "Professionelle Hilfe im Verkehrsrecht",
  },
  trafficAccident: {
    createCase: false,
    lpLink: "/lp/verkehrsunfall",
    buttonLabel: "Professionelle Hilfe im Verkehrsrecht",
  },
  rentalContract: {
    createCase: true,
    lpLink: null,
    buttonLabel: "Erstberatung im Mietrecht",
  },
  generalCivilLaw: {
    createCase: true,
    lpLink: null,
    buttonLabel: "Erstberatung im Zivilrecht",
  },
  employmentContract: {
    createCase: true,
    lpLink: null,
    buttonLabel: "Erstberatung im Arbeitsrecht",
  },
  civilServiceLaw: {
    createCase: true,
    lpLink: null,
    buttonLabel: "Erstberatung im Beamtenrecht",
  },
};

const getPredictedLegalProductButton = (aiResponse: AdvocardFlexAiResponse, createCase: Function, isLoading: boolean) => {
  if (aiResponse?.predictedLegalProduct && aiResponse?.predictedLegalProduct !== "nonSupportedProduct") {
    const buttonConfig = productConfig[aiResponse.predictedLegalProduct];

    if (buttonConfig) {
      if (buttonConfig.lpLink) {
        return (
          <Button fullWidth component={Link} variant={"contained"} to={buttonConfig.lpLink}>
            {buttonConfig.buttonLabel}
          </Button>
        );
      }
      if (buttonConfig.createCase) {
        return (
          <ButtonLoading isLoading={isLoading} fullWidth variant={"contained"} onClick={() => createCase()}>
            {buttonConfig.buttonLabel}
          </ButtonLoading>
        );
      }
    }
  }

  return null;
};

const getDisplayTextByAiResponse = (aiResponse: AdvocardFlexAiResponse) => {
  if (aiResponse.isQuestionCurrentFlexServiceRelated) {
    return (
      "<p>" +
      aiResponse.answer +
      "</p>" +
      "<p>Gerne können Sie über den Button am Ende der Seite ein kostenloses telefonisches Erstberatungsgespräch bei uns buchen.</p>"
    );
  }

  if (aiResponse.predictedLegalProduct === "nonSupportedProduct") {
    return '<p>Bei diesem Anliegen können wir Ihnen an dieser Stelle leider nicht weiterhelfen. Wenden Sie sich bitte direkt an den <a href="https://www.advocard.de/service/" target="_blank">Advocard-Kundenservice</a>.</p>';
  }

  if (aiResponse.predictedLegalProduct) {
    return (
      "<p>Ihre Frage scheint sich auf den Bereich " +
      getProductData(aiResponse.predictedLegalProduct, "productCategory.label") +
      " zu beziehen. Zwar können wir daher Ihre Frage jetzt nicht direkt hier beantworten, Ihnen aber natürlich trotzdem sehr gerne weiterhelfen. Klicken Sie dazu auf den untenstehenden Button, um eine Beratung im entsprechenden Bereich zu erhalten.</p>"
    );
  }

  if (aiResponse.isInsuranceRelatedQuestion) {
    return '<p>Für Details zu Ihrem Versicherungsvertrag wenden Sie sich bitte direkt an den <a href="https://www.advocard.de/service/" target="_blank">Advocard-Kundenservice</a>. Generell gilt: Eine Erstberatung ist in Ihrem Tarif immer gedeckt und für jegliches weiteres Vorgehen wird zunächst eine Deckungsanfrage gestellt.</p>';
  }

  return '<p>Bei diesem Anliegen können wir Ihnen an dieser Stelle nicht weiterhelfen. Wenden Sie sich bitte direkt an den <a href="https://www.advocard.de/service/" target="_blank">Advocard-Kundenservice</a></p>';
};

export function getFlexLpConfigByAiAnswer(aiResponse: AdvocardFlexAiResponse, createCase: Function, isLoading: boolean) {
  return {
    predictedLegalProductButton: getPredictedLegalProductButton(aiResponse, createCase, isLoading),
    textToShow: getDisplayTextByAiResponse(aiResponse),
  };
}

export function isFlexServiceProduct(product: AbstractCase): boolean {
  return product.caseCategory === "advisory" && (product.caseInsurance.specialTermsTag === "advocardFlex" || product.landingPage === "FlexService");
}

export function isCurrentFlexServiceProduct(product: AbstractCase): boolean {
  return product.caseCategory === "advisory" && product.caseInsurance.specialTermsTag === "advocardFlex";
}

export function useFlexServiceLayoutForCustomer(customer: Customer | null): boolean {
  return (customer?.roles && customer.roles.includes("ROLE_ADVOCARD_FLEX_SERVICE")) || false;
}
